import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { AuthenticateSelect } from '../../../../auth/select';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { Textarea } from '../../../../form/textarea';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Main, Styled } from '../teams/style';

const sharePointOperations = [
  'search_sites', 'get_site', 'list_libraries',
  'list_children', 'search_files', 'get_item', 'download_file', 'upload_file', 'create_folder',
  // 'rename_item', 'move_item',
  'delete_item',
  'list_lists', 'get_list', 'create_list', 'update_list', 'delete_list',
  'list_columns', 'get_column', 'create_column', 'update_column', 'delete_column',
  'list_list_items', 'get_list_item', 'create_list_item', 'update_list_item', 'delete_list_item',
  'list_pages', 'get_page', 'create_page', 'update_page', 'publish_page', 'delete_page',
  'agent_decide'
];
const oneDriveOperations = ['list_drives', 'list_shared_items', 'list_children', 'search_files', 'get_item', 'download_file', 'upload_file', 'create_folder',
  //  'rename_item','move_item', 
  'delete_item', 'agent_decide'];
const driveRequiredOperations = ['list_children', 'search_files', 'get_item', 'download_file', 'upload_file', 'create_folder',
  // 'rename_item', 'move_item', 
  'delete_item'];
const itemRequiredOperations = ['list_children', 'get_item', 'download_file',
  // 'rename_item', 'move_item',
  'delete_item'];
const listRequiredOperations = ['get_list', 'update_list', 'delete_list', 'list_columns', 'get_column', 'create_column', 'update_column', 'delete_column', 'list_list_items', 'get_list_item', 'create_list_item', 'update_list_item', 'delete_list_item'];
const title = (value: string) => value.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
const defaultCreatePageDefinitionText = JSON.stringify({
  '@odata.type': '#microsoft.graph.sitePage',
  name: 'my-page.aspx',
  title: 'My Page',
  pageLayout: 'article',
  showComments: true,
  showRecommendedPages: false
}, null, 2);
const defaultCreateColumnDefinitionText = JSON.stringify({
  name: 'projectStatus',
  displayName: 'Project Status',
  text: {}
}, null, 2);
const defaultCreateListDefinitionText = JSON.stringify({
  displayName: 'New List',
  list: {
    template: 'genericList'
  }
}, null, 2);

type Props = NodeProps<operationType> & { provider: 'sharepoint' | 'onedrive'; setChildMinimize?: any; setChildSettings?: any };
type LoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';
type ResourceKey = 'sites' | 'lists' | 'drives' | 'items';
type SharePointResourceGroupId = 'site' | 'library' | 'file' | 'list' | 'column' | 'list_item' | 'page' | 'assistant';

const sharePointResourceGroups: { id: SharePointResourceGroupId; name: string; operations: string[] }[] = [
  { id: 'site', name: 'Site', operations: ['search_sites', 'get_site'] },
  { id: 'library', name: 'Library', operations: ['list_libraries'] },
  {
    id: 'file', name: 'File / Folder', operations: ['list_children', 'search_files', 'get_item', 'download_file', 'upload_file', 'create_folder',
      //  'rename_item',
      'move_item', 'delete_item']
  },
  { id: 'list', name: 'List', operations: ['list_lists', 'get_list', 'create_list', 'update_list', 'delete_list'] },
  { id: 'column', name: 'Column', operations: ['list_columns', 'get_column', 'create_column', 'update_column', 'delete_column'] },
  { id: 'list_item', name: 'List Item', operations: ['list_list_items', 'get_list_item', 'create_list_item', 'update_list_item', 'delete_list_item'] },
  { id: 'page', name: 'Page', operations: ['list_pages', 'get_page', 'create_page', 'update_page', 'publish_page', 'delete_page'] },
  { id: 'assistant', name: 'Assistant', operations: ['agent_decide'] }
];

const getDefaultDefinitionText = (operation: string) => {
  if (['create_page', 'update_page'].includes(operation)) return defaultCreatePageDefinitionText;
  if (['create_column', 'update_column'].includes(operation)) return defaultCreateColumnDefinitionText;
  if (['create_list', 'update_list'].includes(operation)) return defaultCreateListDefinitionText;
  return '';
};

const getSharePointResourceGroupForOperation = (operation: string): SharePointResourceGroupId => {
  return sharePointResourceGroups.find(group => group.operations.includes(operation))?.id || 'site';
};

const getSharePointActionLabel = (operation: string) => {
  const labels: Record<string, string> = {
    search_sites: 'Search',
    get_site: 'Get',
    list_libraries: 'List libraries',
    list_children: 'List children',
    search_files: 'Search',
    get_item: 'Get',
    download_file: 'Download',
    upload_file: 'Upload',
    create_folder: 'Create folder',
    // rename_item: 'Rename',
    move_item: 'Move',
    delete_item: 'Delete',
    list_lists: 'List',
    get_list: 'Get',
    create_list: 'Create',
    update_list: 'Update',
    delete_list: 'Delete',
    list_columns: 'List',
    get_column: 'Get',
    create_column: 'Create',
    update_column: 'Update',
    delete_column: 'Delete',
    list_list_items: 'List',
    get_list_item: 'Get',
    create_list_item: 'Create',
    update_list_item: 'Update',
    delete_list_item: 'Delete',
    list_pages: 'List',
    get_page: 'Get',
    create_page: 'Create',
    update_page: 'Update',
    publish_page: 'Publish',
    delete_page: 'Delete',
    agent_decide: 'Agent decide'
  };
  return labels[operation] || title(operation);
};

const baseButtonStyle = {
  borderRadius: 10,
  border: '1px solid #d5d8dc',
  background: '#fff',
  color: '#1f2937',
  cursor: 'pointer',
  fontSize: 12,
  padding: '7px 10px'
} as const;

const resourceCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  padding: 12,
  marginTop: 12
} as const;

const resourceHintStyle = {
  fontSize: 12,
  color: '#5b6470',
  margin: '6px 0 0'
} as const;

const resourceErrorStyle = {
  fontSize: 12,
  color: '#c2410c',
  margin: '6px 0 0'
} as const;

export function MicrosoftFilesNode({ id, data, provider, setChildMinimize, setChildSettings }: Props) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [expanded, setExpanded] = React.useState(false);
  const [capabilities, setCapabilities] = React.useState<any>(null);
  const [actionError, setActionError] = React.useState('');
  const [siteSearchInput, setSiteSearchInput] = React.useState('');
  const [loadedSites, setLoadedSites] = React.useState<selectDataType[]>([]);
  const [resourceState, setResourceState] = React.useState<Record<ResourceKey, { status: LoadState; error: string }>>({
    sites: { status: 'idle', error: '' },
    lists: { status: 'idle', error: '' },
    drives: { status: 'idle', error: '' },
    items: { status: 'idle', error: '' }
  });
  const connectionId = data.value?.connectionId || data.value?.connection?.id || data.value?.connection?._id || '';
  const operations = provider === 'sharepoint' ? sharePointOperations : oneDriveOperations;
  const operation = data.value?.request || operations[0];
  const sharePointResourceGroup = provider === 'sharepoint' ? getSharePointResourceGroupForOperation(operation) : null;
  const siteId = data.value?.siteId || '';
  const driveId = data.value?.driveId || '';
  const listId = data.value?.listId || '';
  const itemId = data.value?.itemId || '';
  const siteOptions = withActiveOption(loadedSites.length ? loadedSites : data.value?.sites || [], siteId);
  const driveOptions = withActiveOption(data.value?.drives || [], driveId);
  const listOptions = withActiveOption(data.value?.lists || [], listId);
  const itemOptions = withActiveOption(data.value?.cachedItems || data.value?.items || [], itemId);
  const siteRequestRef = React.useRef(0);
  const listRequestRef = React.useRef(0);
  const driveRequestRef = React.useRef(0);
  const itemRequestRef = React.useRef(0);
  const autoSiteLoadKeyRef = React.useRef('');
  const autoSiteResourcesKeyRef = React.useRef('');
  const autoDriveItemsKeyRef = React.useRef('');
  const operationsRef = React.useRef(operations);
  operationsRef.current = operations;

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setExpanded((value: boolean) => !value));
    if (setChildSettings) setChildSettings(() => setSettings(value => ({ ...value, isToggle: !value.isToggle })));
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) updateNodeValue(id, setNodes, { request: operationsRef.current[0], connection: null, connectionId: null, conflictBehavior: 'fail', limit: 50 });
  }, [data.value, id, setNodes]);

  React.useEffect(() => {
    setSiteSearchInput('');
  }, [connectionId]);

  React.useEffect(() => {
    if (!connectionId) {
      setCapabilities(null);
      setActionError('');
      setLoadedSites([]);
      autoSiteLoadKeyRef.current = '';
      autoSiteResourcesKeyRef.current = '';
      setResourceState({
        sites: { status: 'idle', error: '' },
        lists: { status: 'idle', error: '' },
        drives: { status: 'idle', error: '' },
        items: { status: 'idle', error: '' }
      });
      return;
    }

    axios.get(`${webURL}/microsoft-files/connections/${connectionId}/capabilities`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      params: { t: Date.now() }
    })
      .then(response => setCapabilities(response.data?.capabilities))
      .catch(() => setCapabilities(null));
  }, [connectionId]);

  const update = React.useCallback((values: any) => updateNodeValue(id, setNodes, values), [id, setNodes]);
  const change = (event: any, key?: string) => {
    if (!key) return;
    const value = typeof event === 'string' ? event : event.target.value;
    if (key === 'siteUrl') update({ siteUrl: value, validatedSiteName: '' });
    else update({ [key]: value });
  };

  React.useEffect(() => {
    if (provider !== 'sharepoint') return;

    const currentDefinitionText = typeof data.value?.definitionText === 'string' ? data.value.definitionText : '';
    const nextDefaultDefinitionText = getDefaultDefinitionText(operation);
    const isAutoGeneratedTemplate = [
      defaultCreatePageDefinitionText,
      defaultCreateColumnDefinitionText,
      defaultCreateListDefinitionText
    ].includes(currentDefinitionText);

    if (!nextDefaultDefinitionText) {
      if (isAutoGeneratedTemplate) update({ definitionText: '' });
      return;
    }

    if (!currentDefinitionText.trim() || (isAutoGeneratedTemplate && currentDefinitionText !== nextDefaultDefinitionText)) {
      update({ definitionText: nextDefaultDefinitionText });
    }
  }, [data.value?.definitionText, operation, provider, update]);

  const resetSiteScopedFields = React.useCallback(() => ({
    siteUrl: '',
    siteId: '',
    validatedSiteName: '',
    sites: [],
    drives: [],
    driveId: '',
    lists: [],
    listId: '',
    pageId: '',
    itemId: '',
    cachedItems: [],
    items: undefined,
    parentItemId: '',
    destinationParentId: '',
    listItemId: '',
    columnId: ''
  }), []);

  const resetSiteResourceFields = React.useCallback(() => ({
    drives: [],
    driveId: '',
    lists: [],
    listId: '',
    pageId: '',
    itemId: '',
    cachedItems: [],
    items: undefined,
    parentItemId: '',
    destinationParentId: '',
    listItemId: '',
    columnId: ''
  }), []);

  const setSectionState = React.useCallback((key: ResourceKey, status: LoadState, error = '') => {
    setResourceState(current => ({ ...current, [key]: { status, error } }));
  }, []);

  const auth = (type: 'add' | 'remove', connection?: any) => {
    if (type === 'remove') {
      setActionError('');
      autoSiteLoadKeyRef.current = '';
      autoSiteResourcesKeyRef.current = '';
      update({ connection: null, connectionId: null, ...resetSiteScopedFields() });
      return;
    }

    const nextConnectionId = connection?.id || connection?._id || null;
    if (nextConnectionId === connectionId) {
      update({ connection, connectionId: nextConnectionId });
      return;
    }

    setActionError('');
    setLoadedSites([]);
    autoSiteLoadKeyRef.current = '';
    autoSiteResourcesKeyRef.current = '';
    setResourceState({
      sites: { status: 'idle', error: '' },
      lists: { status: 'idle', error: '' },
      drives: { status: 'idle', error: '' },
      items: { status: 'idle', error: '' }
    });
    update({ connection, connectionId: nextConnectionId, ...resetSiteScopedFields() });
  };

  const requestItems: selectDataType[] = operations.map(value => ({ id: value, refId: value, name: title(value), isActive: value === operation }));
  const sharePointResourceItems: selectDataType[] = provider === 'sharepoint'
    ? sharePointResourceGroups.map(group => ({ id: group.id, refId: group.id, name: group.name, isActive: group.id === sharePointResourceGroup }))
    : [];
  const sharePointActionItems: selectDataType[] = provider === 'sharepoint'
    ? (sharePointResourceGroups.find(group => group.id === sharePointResourceGroup)?.operations || []).map(value => ({
      id: value,
      refId: value,
      name: getSharePointActionLabel(value),
      isActive: value === operation
    }))
    : [];
  const conflictItems: selectDataType[] = ['fail', 'replace', 'rename'].map(value => ({ id: value, refId: value, name: title(value), isActive: value === (data.value?.conflictBehavior || 'fail') }));
  const needs = (values: string[]) => values.includes(operation);
  const destructive = needs(['rename_item', 'move_item', 'delete_item', 'update_list', 'delete_list', 'update_column', 'delete_column', 'update_list_item', 'delete_list_item', 'update_page', 'publish_page', 'delete_page']);
  const canBrowseSites = provider === 'sharepoint' && capabilities?.siteInputMode !== 'manual';
  const showDriveSelector = provider === 'onedrive' ? needs(driveRequiredOperations) : needs(driveRequiredOperations);
  const showItemSelector = needs(itemRequiredOperations);
  const showListSelector = provider === 'sharepoint' && needs(listRequiredOperations);
  const handleOperationSelect = React.useCallback((request: string) => {
    update({ request });
  }, [update]);
  const handleSharePointResourceSelect = React.useCallback((groupId: SharePointResourceGroupId) => {
    const nextGroup = sharePointResourceGroups.find(group => group.id === groupId);
    if (!nextGroup?.operations.length) return;
    update({ request: nextGroup.operations[0] });
  }, [update]);

  const postAction = React.useCallback(async (browseOperation: string, payload?: any) => {
    const { items: _items, cachedItems: _cachedItems, bulkItemsText: _bulkItemsText, ...baseValue } = data.value || {};
    const response = await axios.post(`${webURL}/microsoft-files/actions/${provider}`, { ...baseValue, ...(payload || {}), connectionId, operation: browseOperation });
    if (response.data?.success === false) {
      throw new Error(response.data?.errors?.[0]?.message || `Microsoft ${provider} ${browseOperation} failed`);
    }
    return response.data?.data;
  }, [connectionId, data.value, provider]);

  const loadSites = React.useCallback(async (query: string, mode: 'auto' | 'manual' = 'manual') => {
    if (!connectionId || provider !== 'sharepoint' || capabilities?.siteInputMode === 'manual') return;

    const requestId = ++siteRequestRef.current;
    setSectionState('sites', 'loading');
    if (mode === 'manual') setActionError('');

    try {
      const nextQuery = query.trim();
      const responseData = await postAction('search_sites', { query: nextQuery });
      if (siteRequestRef.current !== requestId) return;

      const items = Array.isArray(responseData?.items) ? responseData.items : [];
      const nextSiteId = items.some((item: any) => item.id === siteId) ? siteId : '';
      const nextSites = toSelectOptions(items, nextSiteId, (item: any) => getOptionLabel(item));
      setLoadedSites(nextSites);
      update({ sites: nextSites, siteId: nextSiteId });
      setSectionState('sites', items.length ? 'ready' : 'empty');
    } catch (error: any) {
      if (siteRequestRef.current !== requestId) return;

      const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message || 'Unable to load SharePoint sites';
      setLoadedSites([]);
      setSectionState('sites', 'error', message);
      if (mode === 'manual') setActionError(message);
    }
  }, [capabilities?.siteInputMode, connectionId, postAction, provider, setSectionState, siteId, update]);

  const loadSiteResources = React.useCallback(async (nextSiteId: string) => {
    if (!connectionId || provider !== 'sharepoint' || !nextSiteId) return;

    const nextDriveRequest = ++driveRequestRef.current;
    const nextListRequest = ++listRequestRef.current;
    setSectionState('drives', 'loading');
    setSectionState('lists', 'loading');

    const loadLibraries = async () => {
      try {
        const responseData = await postAction('list_libraries', { siteId: nextSiteId });
        if (driveRequestRef.current !== nextDriveRequest) return;
        const items = Array.isArray(responseData?.items) ? responseData.items : [];
        const nextDriveId = items.some((item: any) => item.id === driveId) ? driveId : '';
        update({ drives: toSelectOptions(items, nextDriveId, (item: any) => getOptionLabel(item)), driveId: nextDriveId });
        setSectionState('drives', items.length ? 'ready' : 'empty');
      } catch (error: any) {
        if (driveRequestRef.current !== nextDriveRequest) return;
        const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message || 'Unable to load document libraries';
        update({ drives: [], driveId: '' });
        setSectionState('drives', 'error', message);
      }
    };

    const loadLists = async () => {
      try {
        const responseData = await postAction('list_lists', { siteId: nextSiteId });
        if (listRequestRef.current !== nextListRequest) return;
        const items = Array.isArray(responseData?.items) ? responseData.items : [];
        const nextListId = items.some((item: any) => item.id === listId) ? listId : '';
        update({ lists: toSelectOptions(items, nextListId, (item: any) => getOptionLabel(item)), listId: nextListId });
        setSectionState('lists', items.length ? 'ready' : 'empty');
      } catch (error: any) {
        if (listRequestRef.current !== nextListRequest) return;
        const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message || 'Unable to load SharePoint lists';
        update({ lists: [], listId: '' });
        setSectionState('lists', 'error', message);
      }
    };

    await Promise.allSettled([loadLibraries(), loadLists()]);
  }, [connectionId, driveId, listId, postAction, provider, setSectionState, update]);

  const loadOneDriveDrives = React.useCallback(async () => {
    if (!connectionId) return;
    const requestId = ++driveRequestRef.current;
    setSectionState('drives', 'loading');
    setActionError('');
    try {
      const responseData = await postAction('list_drives');
      if (driveRequestRef.current !== requestId) return;
      const items = Array.isArray(responseData?.items) ? responseData.items : [];
      const nextDriveId = items.some((item: any) => item.id === driveId) ? driveId : '';
      update({ drives: toSelectOptions(items, nextDriveId, (item: any) => getOptionLabel(item)), driveId: nextDriveId });
      setSectionState('drives', items.length ? 'ready' : 'empty');
    } catch (error: any) {
      if (driveRequestRef.current !== requestId) return;
      const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message || 'Unable to load drives';
      setActionError(message);
      setSectionState('drives', 'error', message);
    }
  }, [connectionId, driveId, postAction, setSectionState, update]);

  const loadDriveItems = React.useCallback(async (nextDriveId: string) => {
    if (!connectionId || !nextDriveId) return;
    const requestId = ++itemRequestRef.current;
    setSectionState('items', 'loading');
    try {
      const responseData = await postAction('list_children', { driveId: nextDriveId, itemId: '' });
      if (itemRequestRef.current !== requestId) return;
      const items = Array.isArray(responseData?.items) ? responseData.items : [];
      const nextItemId = items.some((item: any) => item.id === itemId) ? itemId : '';
      update({
        cachedItems: toSelectOptions(items, nextItemId, (item: any) => `${getOptionLabel(item)}${isFolderLike(item) ? ' (Folder)' : ''}`),
        items: undefined,
        itemId: nextItemId
      });
      setSectionState('items', items.length ? 'ready' : 'empty');
    } catch (error: any) {
      if (itemRequestRef.current !== requestId) return;
      const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message || 'Unable to load library items';
      update({ items: [], itemId: '' });
      setSectionState('items', 'error', message);
    }
  }, [connectionId, itemId, postAction, setSectionState, update]);

  const selectSite = React.useCallback((nextSiteId: string) => {
    const nextSite = siteOptions.find(item => item.id === nextSiteId);
    autoSiteResourcesKeyRef.current = '';
    update({
      siteId: nextSiteId,
      validatedSiteName: nextSite?.name || data.value?.validatedSiteName || '',
      ...resetSiteResourceFields()
    });
  }, [data.value?.validatedSiteName, resetSiteResourceFields, siteOptions, update]);

  const selectDrive = React.useCallback((nextDriveId: string) => {
    autoDriveItemsKeyRef.current = '';
    update({
      driveId: nextDriveId,
      cachedItems: [],
      items: undefined,
      itemId: '',
      parentItemId: '',
      destinationParentId: ''
    });
  }, [update]);

  React.useEffect(() => {
    if (!canBrowseSites || !connectionId) return;
    const nextKey = `${provider}:${connectionId}:${capabilities?.siteInputMode || 'default'}`;
    if (autoSiteLoadKeyRef.current === nextKey) return;
    autoSiteLoadKeyRef.current = nextKey;
    loadSites('', 'auto');
  }, [canBrowseSites, capabilities?.siteInputMode, connectionId, loadSites, provider]);

  React.useEffect(() => {
    if (!canBrowseSites || !connectionId) return;
    const trimmedQuery = siteSearchInput.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) return;

    const timeoutId = window.setTimeout(() => {
      loadSites(trimmedQuery, 'manual');
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [canBrowseSites, connectionId, loadSites, siteSearchInput]);

  React.useEffect(() => {
    if (provider !== 'sharepoint' || !connectionId || !siteId) {
      autoSiteResourcesKeyRef.current = '';
      return;
    }

    const nextKey = `${connectionId}:${siteId}`;
    if (autoSiteResourcesKeyRef.current === nextKey) return;
    autoSiteResourcesKeyRef.current = nextKey;
    loadSiteResources(siteId);
  }, [connectionId, loadSiteResources, provider, siteId]);

  React.useEffect(() => {
    if (!connectionId || !driveId || !showItemSelector) {
      autoDriveItemsKeyRef.current = '';
      return;
    }

    const nextKey = `${provider}:${connectionId}:${driveId}:${operation}`;
    if (autoDriveItemsKeyRef.current === nextKey) return;
    autoDriveItemsKeyRef.current = nextKey;
    loadDriveItems(driveId);
  }, [connectionId, driveId, loadDriveItems, operation, provider, showItemSelector]);

  return <>
    <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
    <Main >
      <div className='max-w-10'>


        {!connectionId ? <><div id="warning"></div><span id="warningIcon"><IoWarningOutline size={21} /></span></> : data.output ? <><div id="ran"></div><span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span></> : null}
        {expanded ? <ExpandedBlock data={{ id, ...data }} handleClose={() => setExpanded(false)} isWarning={!connectionId} style={{ minWidth: '490px' }}><Styled>

          <AuthenticateSelect type="microsoft" defaultId={connectionId} defaultConnection={data.value?.connection} handleAuthenticate={auth} background={data.background} />
          {actionError ? <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 8px' }}>{actionError}</p> : null}
          {provider === 'sharepoint' ? <>
            <Select title="Resource" data={sharePointResourceItems} handleSelectItem={handleSharePointResourceSelect} isSmall />
            <Select title="Action" data={sharePointActionItems} handleSelectItem={handleOperationSelect} isSmall />
          </> : <Select title="Operation" data={requestItems} handleSelectItem={handleOperationSelect} isSmall />}

          {provider === 'sharepoint' ? <>
            <div>
              {/* {canBrowseSites ? <button style={baseButtonStyle} disabled={resourceState.sites.status === 'loading'} onClick={() => loadSites('*', 'auto')}>Refresh sites</button> : null} */}

              <div style={{ marginBottom: "10px" }}>
                {capabilities?.siteInputMode === 'manual' ? null : <>

                  <TextInput
                    title="Find site"
                    value={siteSearchInput}
                    placeholder="Find a site"
                    handleChange={(event: any) => setSiteSearchInput(event.target.value)}
                    isNormal
                  />
                  <Select
                    title="Site"
                    data={siteOptions}
                    handleSelectItem={selectSite}
                    emptyMsg={resourceState.sites.status === 'loading'
                      ? (siteSearchInput.trim().length >= 2 ? `Searching for "${siteSearchInput.trim()}"...` : 'Loading SharePoint sites...')
                      : (siteSearchInput.trim().length >= 2 ? 'No matching sites found.' : 'Find and select a site')}
                    isSmall
                  />
                  {resourceState.sites.status === 'loading' ? <p style={resourceHintStyle}>Loading SharePoint sites...</p> : null}
                  {resourceState.sites.status === 'empty' ? <p style={resourceHintStyle}>No sites were found automatically. Search by name or validate a site URL below.</p> : null}
                  {resourceState.sites.status === 'error' ? <div style={{ marginTop: 6 }}>
                    <p style={resourceErrorStyle}>{resourceState.sites.error}</p>
                    <button type="button" style={{ ...baseButtonStyle, marginTop: 6 }} onClick={() => loadSites('', 'auto')}>Retry loading sites</button>
                  </div> : null}
                </>}

              </div>
            </div>
            {siteId ? <div >
              {/* <strong style={{ display: 'block', fontSize: 14, color: '#111827' }}>Choose the resource inside this site</strong> */}
              {/* <p style={resourceHintStyle}>
              {showListSelector ? 'Pick a SharePoint list for the current operation.' : showDriveSelector ? 'Pick a document library for the current operation.' : 'This operation only needs the site itself.'}
            </p> */}

              {showListSelector ? <>
                {listOptions.length ? <Select title="List" data={listOptions} handleSelectItem={(nextListId: string) => update({ listId: nextListId })} emptyMsg="Select a list" isSmall /> : null}
                {resourceState.lists.status === 'loading' ? <p style={resourceHintStyle}>Loading lists for this site...</p> : null}
                {resourceState.lists.status === 'empty' ? <p style={resourceHintStyle}>No lists were found for this site.</p> : null}
                {resourceState.lists.status === 'error' ? <div style={{ marginTop: 6 }}>
                  <p style={resourceErrorStyle}>{resourceState.lists.error}</p>
                  <button type="button" style={{ ...baseButtonStyle, marginTop: 6 }} onClick={() => loadSiteResources(siteId)}>Retry loading lists</button>
                </div> : null}
              </> : null}

              {showDriveSelector ? <>
                {driveOptions.length ? <Select title="Library" data={driveOptions} handleSelectItem={selectDrive} emptyMsg="Select a library" isSmall /> : null}
                {resourceState.drives.status === 'loading' ? <p style={resourceHintStyle}>Loading document libraries for this site...</p> : null}
                {resourceState.drives.status === 'empty' ? <p style={resourceHintStyle}>No document libraries were found for this site.</p> : null}
                {resourceState.drives.status === 'error' ? <div style={{ marginTop: 6 }}>
                  <p style={resourceErrorStyle}>{resourceState.drives.error}</p>
                  <button type="button" style={{ ...baseButtonStyle, marginTop: 6 }} onClick={() => loadSiteResources(siteId)}>Retry loading libraries</button>
                </div> : null}
              </> : null}
            </div> : null}
          </> : <>
            {capabilities?.appTargetUserMode === 'manual' ? <TextInput keyRef="targetUser" value={data.value?.targetUser} title="User UPN or Entra object ID" placeholder="user@company.com" handleChange={change} /> : null}
            <div style={resourceCardStyle}>
              <strong style={{ display: 'block', fontSize: 14, color: '#111827' }}>Choose a drive</strong>
              <p style={resourceHintStyle}>Load drives first, then pick the one this node should work with.</p>
              <div className="action">
                <button type="button" className="btn1 withBorder" disabled={resourceState.drives.status === 'loading' || !connectionId} onClick={loadOneDriveDrives}>Load drives</button>
              </div>
              {driveOptions.length ? <Select title="Drive" data={driveOptions} handleSelectItem={(nextDriveId: string) => update({ driveId: nextDriveId })} emptyMsg="Select a drive" isSmall /> : null}
              {resourceState.drives.status === 'loading' ? <p style={resourceHintStyle}>Loading drives...</p> : null}
              {resourceState.drives.status === 'empty' ? <p style={resourceHintStyle}>No drives were returned for this connection.</p> : null}
              {resourceState.drives.status === 'error' ? <p style={resourceErrorStyle}>{resourceState.drives.error}</p> : null}
            </div>
          </>}

          {needs(['search_sites', 'search_files']) ? <TextInput keyRef="query" value={data.value?.query} title="Search query" placeholder="query" handleChange={change} /> : null}
          {showItemSelector ? <>
            {itemOptions.length ? <Select title="Item" data={itemOptions} handleSelectItem={(nextItemId: string) => update({ itemId: nextItemId })} emptyMsg="Select a file or folder" isSmall /> : null}
            {resourceState.items.status === 'loading' ? <p style={resourceHintStyle}>Loading files and folders from the selected library...</p> : null}
            {resourceState.items.status === 'empty' ? <p style={resourceHintStyle}>No files or folders were found in the root of this library. Use the manual ID fallback for nested items.</p> : null}
            {resourceState.items.status === 'error' ? <p style={resourceErrorStyle}>{resourceState.items.error}</p> : null}
            {!itemOptions.length ? <TextInput keyRef="itemId" value={data.value?.itemId} title="Item ID" placeholder="item id" handleChange={change} /> : null}
          </> : null}
          {needs(['upload_file', 'create_folder']) ? <TextInput keyRef="parentItemId" value={data.value?.parentItemId} title="Parent folder ID" placeholder="parent item id" handleChange={change} /> : null}
          {needs(['move_item']) ? <TextInput keyRef="destinationParentId" value={data.value?.destinationParentId} title="Destination folder ID" placeholder="destination parent id" handleChange={change} /> : null}
          {needs(['upload_file', 'create_folder', 'rename_item']) ? <TextInput keyRef="name" value={data.value?.name} title="Name" placeholder="name" handleChange={change} /> : null}
          {operation === 'upload_file' ? <><TextInput keyRef="sourceUrl" value={data.value?.sourceUrl} title="Source file URL" placeholder="https://..." handleChange={change} /><TextInput keyRef="sourceSizeBytes" value={data.value?.sourceSizeBytes} title="Exact size in bytes" placeholder="4000000" type="number" handleChange={change} /><TextInput keyRef="contentType" value={data.value?.contentType} title="Content type" placeholder="application/octet-stream" handleChange={change} /></> : null}
          {needs(['get_list_item', 'update_list_item', 'delete_list_item']) ? <TextInput keyRef="listItemId" value={data.value?.listItemId} title="List item ID" placeholder="list item id" handleChange={change} /> : null}
          {needs(['get_column', 'update_column', 'delete_column']) ? <TextInput keyRef="columnId" value={data.value?.columnId} title="Column ID" placeholder="column id" handleChange={change} /> : null}
          {needs(['get_page', 'update_page', 'publish_page', 'delete_page']) ? <TextInput keyRef="pageId" value={data.value?.pageId} title="Page ID" placeholder="page id" handleChange={change} /> : null}
          {destructive || (operation === 'upload_file' && data.value?.conflictBehavior === 'replace') ? <TextInput keyRef="eTag" value={data.value?.eTag} title="eTag / If-Match" placeholder="current eTag" handleChange={change} /> : null}
          {needs(['upload_file', 'create_folder']) ? <Select title="Name conflict" data={conflictItems} handleSelectItem={(conflictBehavior: string) => update({ conflictBehavior })} isSmall /> : null}
          {needs(['create_list', 'update_list', 'create_column', 'update_column', 'create_list_item', 'update_list_item', 'create_page', 'update_page']) ? <Textarea keyRef="definitionText" value={data.value?.definitionText} placeholder="JSON definition or fields" handleChange={change} /> : null}

        </Styled></ExpandedBlock> : null}
      </div></Main>
  </>;
}

function toSelectOptions(items: any[], selectedId: string, label: (item: any) => string): selectDataType[] {
  return items.map((item: any) => ({
    id: item.id,
    refId: item.id,
    name: label(item),
    isActive: item.id === selectedId
  }));
}

function withActiveOption(data: selectDataType[], selectedId: string): selectDataType[] {
  return data.map(item => ({ ...item, isActive: item.id === selectedId }));
}

function getOptionLabel(item: any): string {
  return item?.displayName
    || item?.name
    || item?.title
    || item?.fields?.FileLeafRef
    || item?.fields?.LinkFilename
    || item?.fields?.Title
    || item?.id;
}

function isFolderLike(item: any): boolean {
  return item?.itemType === 'folder'
    || item?.folder
    || item?.contentType?.name === 'Folder'
    || item?.fields?.ContentType === 'Folder';
}
